import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { type SSEController, subscribeToSse } from '@/features/api/utils/sse.util';
import { useSession } from '@/features/auth/context/SessionContext';
import { dealsKeyFactory } from '@/features/locations/services/deals.api';
import { useMixpanel } from '@/hooks/useMixpanel';
import { parseBackendError } from '@/libs/utils/parseBackendErrors';
import type { ICreateLocationDealRequest, IDealCreationProgress } from '@/types/api';
import { useStartDealCreation } from '../services/dealCreation.api';
import {
  DEALS_MIXPANEL_EVENTS,
  type DealCreationFailedPayload,
  type DealCreationSubmittedPayload,
  type DealCreationSucceededPayload,
} from '../utils/mixpanelEvents';

export interface DealCreationState {
  /** Null until a run has been started from this dialog. */
  progress: IDealCreationProgress | null;
  isStarting: boolean;
  /** Set when the request itself failed, which is distinct from a run that failed. */
  error: Error | null;
}

const INITIAL_STATE: DealCreationState = { progress: null, isStarting: false, error: null };

/**
 * Start a deal creation for a location and follow it.
 *
 * The POST answers 202 and carries nothing: the deal's id is HubSpot's and does not exist until
 * the run's third step. So the run is followed over SSE on the same path, which reports where it
 * stands and carries the deal itself once it lands. The stream closes on the first terminal
 * reading, and the caller reads `progress.status` to know which.
 */
export function useDealCreation(locationId: string) {
  const [state, setState] = useState<DealCreationState>(INITIAL_STATE);
  const controller = useRef<SSEController | null>(null);
  const start = useStartDealCreation(locationId);
  const queryClient = useQueryClient();
  // The run is this hook's, so what it did belongs here: the request carries what was sent, and
  // the attempt count and duration are the run's own. The form's steps are tracked by the dialog.
  const trackAction = useMixpanel((s) => s.trackAction);
  const { user } = useSession();
  const attemptsRef = useRef(0);
  const runStartedRef = useRef(0);
  // The stream stops on the first terminal reading, but a repeated one must not be counted twice.
  const terminalReportedRef = useRef(false);

  const stop = useCallback(() => {
    controller.current?.stop();
    controller.current = null;
  }, []);

  // The stream outlives a render but not the dialog: leaving it open would keep reading a run
  // nobody is watching.
  useEffect(() => stop, [stop]);

  const reset = useCallback(() => {
    stop();
    setState(INITIAL_STATE);
  }, [stop]);

  const reportTerminal = useCallback(
    (event: string, payload: DealCreationSucceededPayload | DealCreationFailedPayload) => {
      if (terminalReportedRef.current) return;
      terminalReportedRef.current = true;
      trackAction(event, payload);
    },
    [trackAction],
  );

  const submit = useCallback(
    async (body: ICreateLocationDealRequest) => {
      stop();
      setState({ progress: null, isStarting: true, error: null });
      attemptsRef.current += 1;
      runStartedRef.current = Date.now();
      terminalReportedRef.current = false;
      const submitted: DealCreationSubmittedPayload = {
        company_mode: body.company_id ? 'attach' : 'create',
        contact_mode: body.contact_id ? 'attach' : 'create',
        has_owner: body.owner_email !== undefined,
        has_zone_ref: body.zone_ref !== undefined,
        // The owner field defaults to the signed-in BD, so this is whether they left it there.
        owner_is_self:
          body.owner_email !== undefined &&
          user?.email !== undefined &&
          body.owner_email.toLowerCase() === user.email.toLowerCase(),
        attempt: attemptsRef.current,
      };
      trackAction(DEALS_MIXPANEL_EVENTS.CREATION_SUBMITTED, submitted);
      try {
        await start.mutateAsync(body);
      } catch (err) {
        // A location that already carries a deal answers 409, which is the one refusal the BD can
        // act on: the deal is already there.
        const parsed = parseBackendError(err instanceof Error ? err.message : String(err));
        const message =
          parsed.httpCode === 409
            ? 'This location already carries a deal.'
            : (parsed.message ?? 'The creation could not be started.');
        toast.error(message);
        // No step: the run never started, so there is none to name.
        reportTerminal(DEALS_MIXPANEL_EVENTS.CREATION_FAILED, {
          failure_reason: message,
          // 0 is the parser's "no status found", which is not a status to report.
          http_code: parsed.httpCode !== 0 ? parsed.httpCode : undefined,
          run_ms: Date.now() - runStartedRef.current,
          attempt_count: attemptsRef.current,
        });
        setState({ progress: null, isStarting: false, error: new Error(message) });
        return;
      }

      setState({
        progress: { status: 'running' },
        isStarting: false,
        error: null,
      });

      controller.current = subscribeToSse<IDealCreationProgress>(
        { url: `locations/${locationId}/deal` },
        {
          onData(progress, stopStream) {
            setState((prev) => ({ ...prev, progress }));
            if (progress.status === 'running') return;
            stopStream();
            // The deals layer is a 10s poll, so without this the new pin appears up to a poll
            // later — long after the card that is meant to be pointing at it.
            const runMs = Date.now() - runStartedRef.current;
            if (progress.status === 'created') {
              reportTerminal(DEALS_MIXPANEL_EVENTS.CREATION_SUCCEEDED, {
                deal_id: progress.deal_id ?? '',
                run_ms: runMs,
                attempt_count: attemptsRef.current,
              });
              void queryClient.invalidateQueries({ queryKey: dealsKeyFactory.list().queryKey });
            } else {
              reportTerminal(DEALS_MIXPANEL_EVENTS.CREATION_FAILED, {
                step: progress.step,
                failure_reason: progress.message ?? 'The creation stopped.',
                run_ms: runMs,
                attempt_count: attemptsRef.current,
              });
            }
          },
          onError(err, stopStream) {
            // The run carries on without us; only the watching stopped. Reporting it as a failed
            // creation would be a lie, so the reading stays running and the error is surfaced
            // separately.
            setState((prev) => ({ ...prev, error: err }));
            stopStream();
          },
        },
      );
    },
    [locationId, queryClient, start, stop, trackAction, reportTerminal, user?.email],
  );

  return { ...state, submit, reset };
}
